import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import Chart from "react-google-charts";
import { IBusinessCreditReportResponse, BusinessCreditReportStatePII } from '../../../store/businessCreditReportService/types';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import * as Service from '../../../store/service';
import * as BusinessCreditReportAction from '../../../store/businessCreditReportService/actions';
import { getFormattedDate } from '../../../util/dateFormatting';
import i18nService from '../../../util/Internationalization/i18n/i18n';
import { NavLink } from 'react-router-dom';
import RenderIcons from '../../../Assets/css/_icons/partner_icon/RenderIcons';
import { useTranslation } from 'react-i18next';

interface IProps {
    reportObject: BusinessCreditReportStatePII;
    showDetails: boolean;
}

const BCreditScoreTracker: React.FunctionComponent<IProps> = (props) => {
    const { t: tLang } = useTranslation();
    const [month, setMonth] = useState(0);
    const [tableViewDiv, setTableViewDiv] = useState(true);
    const [showTracker, setShowTracker] = useState(true);
    const [chartData, setChartData] = useState([['Month', '']]);
    const [point, setPoint] = useState(0);
    const [ratingDefinitions, setRatingDefinitions] = useState('Not Rated');
    const [riskRating, setRiskRating] = useState('');
    const [tooltipTriggerType, setTooltipTriggerType] = useState('both');
    const [previousScore, setPreviousScore] = useState(0);
    const [currentScore, setCurrentScore] = useState(0);
    const [tooltipTrigger, setTooltiptrigger] = useState(false);

    let options = {
        title: '',
        hAxis: {
            title: '',
            titleTextStyle: {
                color: '#333'
            }
        },
        pointSize: 5,
        vAxis: {
            baselineColor: '#fff',
            gridlineColor: '#fff',
            textPosition: 'none'
        },
        colors: ['#37A954'],
        height: 120,
        legend: {
            position: 'none'
        },
        tooltip: {
            isHtml: true,
            trigger: tooltipTrigger == false ? 'selection' : 'none'
        },
        chartArea: {
            left: 24,
            top: 4, // prevents top-most point on graph from getting cut off
            bottom: 26,
            right: 24,
            width: "70%",
            height: "100%"
        },
        series: {
            0: {
                type: 'AreaChart',
                areaOpacity: 0.1
            },
            1: {
                type: 'AreaChart',
                areaOpacity: 0.1
            }
        }
    };

    const dispatch = useDispatch();
    const requestBusinessCreditReport = (ReportPK: any, forcefullyRefresh: boolean, creditProvider: string) => dispatch(BusinessCreditReportAction.actionCreators.requestBusinessCreditReport(ReportPK, forcefullyRefresh, creditProvider));
    const businessProfileStatus = useSelector((state: ApplicationState) => state.loggedInUser && state.loggedInUser.userServiceDetails?.businessProfileStatus?.filter((obj: any) => { return obj.provider; }));
    const creditReportResponses: IBusinessCreditReportResponse[] = useSelector((state: ApplicationState) => (state.businessCreditReportState && state.businessCreditReportState.creditReportData)!);
    const isLoadingCreditReport: boolean = useSelector((state: ApplicationState) => (state.businessCreditReportState && state.businessCreditReportState.isLoading)!);

    const ensureDataFetched = () => {
        const creditProvider = businessProfileStatus != null && businessProfileStatus.length > 0 ? businessProfileStatus[0].provider : '';
        requestBusinessCreditReport(null, false, creditProvider);
        let dataForChart: any = [];
        if (chartData.length === 1) {
            const parser = new DOMParser();
            const strToDecode = 'Creditsafe';
            const decodedString = parser.parseFromString(`<!doctype html><body>${strToDecode}`, 'text/html').body.textContent;
            dataForChart.push(['Month', decodedString]);

            if (!isLoadingCreditReport) {
                Service.getBusinessCreditTracker(creditProvider).then(data => {
                    data = JSON.parse(data);

                    if (data.HasError == false && data.Score) {
                        data.Score.sort(function (a: any, b: any) {
                            let aScoreTime = moment(a.CreatedDate, 'MM/D/YYYY').toDate().getTime();
                            let bScoreTime = moment(b.CreatedDate, 'MM/D/YYYY').toDate().getTime();
                            return aScoreTime - bScoreTime;
                        });
                        if (data.Score.length === 1) {
                            dataForChart.push([
                                '',
                                Number(data.Score[0].Value)]
                            );
                            setTooltipTriggerType('none');
                        }
                        else { setTooltipTriggerType('both'); }


                        data.Score.forEach((score: any) => {
                            dataForChart.push([
                                moment(score.DateOfLatestRatingChange, 'YYYY-MM-DD').format('MMM DD, YYYY'),
                                Number(score.Value)]
                            );
                        });

                        setChartData(dataForChart);
                        PrintScoreDiff(dataForChart);
                        getRiskRating(dataForChart[dataForChart.length - 1][1]);
                    }
                    else {
                        setShowTracker(false);
                    }
                }).catch(function (error) {

                });
            }
        }
    }

    const getRiskRating = (point: number) => {
        if (point >= 0 && point <= 20) {
            setRiskRating('D/E');
            setRatingDefinitions('Very High Risk');
        }
        else if (point >= 21 && point <= 29) {
            setRiskRating('D');
            setRatingDefinitions('High Risk');
        }
        else if (point >= 30 && point <= 50) {
            setRiskRating('C');
            setRatingDefinitions('Moderate Risk');
        }
        else if (point >= 51 && point <= 70) {
            setRiskRating('B');
            setRatingDefinitions('Low Risk');
        }
        else if (point >= 71 && point <= 100) {
            setRiskRating('A');
            setRatingDefinitions('Very Low Risk');
        }
    }

    const PrintScoreDiff = (tmpChartData: []) => {
        let currentScore: any = 0;
        let previousScore: any = 0;

        if (tmpChartData.length > 1) {
            setPoint(parseInt(tmpChartData[tmpChartData.length - 1][1]));
            currentScore = tmpChartData[tmpChartData.length - 1][1];
        }
        if (tmpChartData.length > 2 && tmpChartData[tmpChartData.length - 1][0] != "" && tmpChartData[tmpChartData.length - 2][0] != "") {
            previousScore = tmpChartData[tmpChartData.length - 2][1];
            var lastPoint = tmpChartData[tmpChartData.length - 1][0];
            var secondLastPoint = tmpChartData[tmpChartData.length - 2][0];

            var getMonth = moment(new Date(lastPoint)).diff(new Date(secondLastPoint), 'months', true);
            getMonth = Math.floor(getMonth);
            setMonth(getMonth);
        }
        setPreviousScore(previousScore);
        setCurrentScore(currentScore);
    }

    useEffect(ensureDataFetched, [creditReportResponses]);

    if (showTracker) {
        return (
            <React.Fragment>
                {chartData.length > 1 ?
                    <div className="box offset-padding">
                        <div className="box-header">

                            <div className="box-title-container credit-container">
                                <div className="box-title-icon" aria-hidden="true"><RenderIcons iconName='chart-line' /></div>
                                <div className="box-title-text">
                                    <h2 className="box-title" id="credit-score-trend-title">{tLang('dashboard.creditScore.trackerTitle')}</h2>
                                    <div>
                                        {props.showDetails === false &&
                                            <div className="box-actions">
                                                <Link to="/credit/score" className="default_link">Find out more</Link>
                                            </div>
                                        }
                                        {tableViewDiv === true ? (
                                            <div onClick={() => setTableViewDiv(false)} className="box-actions credit-score-top">
                                                <NavLink to="#" className="default_link">
                                                    <span className="nav-text">
                                                        {tLang('credit.creditScore.creditScoreTrackerWidget.label.tableScoreView')}
                                                    </span>
                                                </NavLink>
                                            </div>
                                        ) : (
                                            <div onClick={() => setTableViewDiv(true)} className="box-actions credit-score-top">
                                                <NavLink to="#" className="default_link">
                                                    <span className="nav-text">
                                                        {tLang('credit.creditScore.creditScoreTrackerWidget.label.cartScoreView')}
                                                    </span>
                                                </NavLink>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="box-description"></div>
                            <div className="box-actions"></div>
                        </div>
                        <div className="box-content">
                            <div className="m-credit-score-trend-container">
                                <div className="credit-score-trend status-good">
                                    <h2>
                                        <div className="credit-score-trend-value" aria-labelledby="credit-score-trend-title">{point}</div>
                                    </h2>
                                    {currentScore > previousScore &&
                                        <div className="credit-score-trend-change status-up">
                                            +{currentScore - previousScore} points in <abbr title={month + " Months"}>{month}M</abbr>
                                        </div>
                                    }
                                    {currentScore < previousScore &&
                                        <div className="credit-score-trend-change status-down">
                                            -{previousScore - currentScore} points in <abbr title={month + " Months"}>{month}M</abbr>
                                        </div>
                                    }
                                    {currentScore === previousScore &&
                                        <div className="credit-score-trend-change status-unchanged">
                                            -{previousScore - currentScore} points in <abbr title={month + " Months"}>{month}M</abbr>
                                        </div>
                                    }
                                    <div className="credit-score-trend-rating">{ratingDefinitions}</div>
                                </div>
                                <div className="credit-score-trend-chart">
                                    <div id="chart-credit-trend" className="m-chart-container">
                                        {tableViewDiv === false ? (
                                            <table className="svg-table">
                                                <tbody>
                                                    {chartData.map((index, key) => (

                                                        { index }.index[0].length > 0 &&
                                                        <tr>
                                                            <td className="google-visualization-table-tr-head">{index[0]}</td>
                                                            <td>{index[1]}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <Chart
                                                chartType="AreaChart"
                                                loader={<div>Loading Chart</div>}
                                                data={chartData}
                                                options={options}
                                                    rootProps={{ 'data-testid': '2' }}
                                                    chartEvents={[
                                                        {
                                                            eventName: "ready",
                                                            callback: ({ chartWrapper, google }) => {
                                                                var chart = new google.visualization.AreaChart(document.getElementById('chart-credit-trend'));
                                                                var svg = chart.getContainer().getElementsByTagName('svg');
                                                                if (svg.length > 0) {
                                                                    svg[0].setAttribute('aria-label', 'Credit Activity Bar Graph');
                                                                    svg[0].setAttribute('role', 'img');
                                                                }
                                                                document.addEventListener('keydown', (event) => {
                                                                    if (event.key === 'Escape') {
                                                                        setTooltiptrigger(true);
                                                                        event.stopPropagation();
                                                                        setTooltiptrigger(false);
                                                                    }
                                                                });
                                                                const datachart = document.getElementsByClassName('google-visualization-tooltip');
                                                                document.addEventListener('mousedown', (event) => {
                                                                    if (datachart.length != 0) {
                                                                        if (event instanceof MouseEvent) {
                                                                            setTooltiptrigger(true);
                                                                            setTooltiptrigger(false);
                                                                        }
                                                                    }
                                                                });
                                                            }
                                                        }
                                                    ]}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            {props.showDetails && chartData.length > 1 &&
                                <React.Fragment>
                                    <div className="line-divider margin-vertical-xs"></div>
                                    <div className="row between-sm score-details">
                                        <div className="col-xs-12 col-sm col-no-gutter flex-grow-null">
                                            <div className="data-row condensed row">
                                                <div className="col-xs col-sm-12 col-no-gutter">
                                                    <div className="data-label" id="ma-summary-employees">Last Changed</div>
                                                </div>
                                                <div className="col-xs col-sm-12 col-no-gutter flex-grow-null-xs">
                                                    <div className="data-value no-padding " aria-labelledby="ma-summary-employees">
                                                        <span className="emphasize-3">
                                                            {props.reportObject?.businessCompanyReport?.credit_score_and_limit?.date_of_latest_rating_Change && getFormattedDate(i18nService.get('Country'), props.reportObject?.businessCompanyReport?.credit_score_and_limit?.date_of_latest_rating_Change)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-xs-12 col-sm col-no-gutter flex-grow-null">
                                            <div className="data-row condensed row">
                                                <div className="col-xs col-sm-12 col-no-gutter">
                                                    <div className="data-label" id="ma-summary-devices">Score Rating</div>
                                                </div>
                                                <div className="col-xs col-sm-12 col-no-gutter flex-grow-null-xs">
                                                    <div className="data-value no-padding " aria-labelledby="ma-summary-devices">
                                                        <span className="emphasize-3">{riskRating}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-xs-12 col-sm col-no-gutter text-align-right-sm flex-grow-null">
                                            <div className="data-row condensed row">
                                                <div className="col-xs col-sm-12 col-no-gutter">
                                                    <div className="data-label" id="ma-summary-risks">Credit Limit</div>
                                                </div>
                                                <div className="col-xs col-sm-12 col-no-gutter flex-grow-null-xs">
                                                    <div className="data-value no-padding " aria-labelledby="ma-summary-risks">
                                                        <span className="emphasize-3">
                                                            {props.reportObject?.businessCompanyReport?.credit_score_and_limit?.current_recommended_credit_limit}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </React.Fragment>
                            }
                        </div>
                    </div>
                    : <div className="box loading"></div>}
            </React.Fragment>
        );
    }
    else { return null; }
}

export default BCreditScoreTracker;
